import type { IVehicleEditableProperties } from '../../../../Domain/Vehicle/IVehicle';
import { HttpBase } from '@library/Security';
import type { IVehicleConditionDto } from '@domain/Vehicle';
import { useUser } from '@domain/Security/User';
import type { IErrorCodeDTO } from '@domain/CarePlan/HealthMonitor/IErrorCode';
import type { IScanEventDto } from '@domain/CarePlan/HealthMonitor/IScanEvent';
import { isNotProduction } from '@modules/Utilities/Environments';
import { useDemoScanErrors, useDemoScanHistory } from './MockData/ScanEvents';
import { useUserSettings } from '@services/UserSettings';

export class VehicleApi extends HttpBase
{
    private getPayload = ( otherParams?: object ) =>
    {
        const user = useUser();
        return {
            vehicleId: user?.activeVehicle.vehicleId,
            ...otherParams
        };
    };

    /**
     * Resets a vehicle and moves it back into an unclaimed state
     * @returns
     */
    async reset(): Promise<boolean>
    {
        const payload = this.getPayload();
        const result = await this.put( 'api/vehicle/reset-vehicle', payload );
        return result.success;
    }

    /**
     * Updates the vehicle details
     * @param request dto model containing the editable vehicle properties to update
     * @returns a vehicleConditionDto containing all the recalculated conditions & service schedule
     */
    async updateVehicleDetailsAsync( request: IVehicleEditableProperties ): Promise<IVehicleConditionDto | null>
    {
        const payload = this.getPayload( request );
        const result = await this.put( 'api/vehicle/', payload );
        return result.success ? result.data : null;
    }

    /**
     * Updates the vehicle's odometer
     * @param odometer the new odometer value
     * @returns a vehicleConditionDto containing all the recalculated conditions & service schedule
     */
    async updateOdometer( odometer: number ): Promise<any>
    {
        const payload = this.getPayload( { odometer } );
        const result = await this.put( 'api/vehicle/odometer', payload );
        return result;
    }

    /**
     * Gets the scan history events
     * @param vehicleId
     * @param tripsRequested
     * @returns
     */
    async getScanHistory( vehicleId: string, tripsRequested: number ): Promise<{scanEvents: IScanEventDto[]}>
    {
        const userSettings = useUserSettings();
        const request = { vehicleId: vehicleId, tripsRequested };
        const result = await this.get( 'api/vehicle/scan-event/list', request );

        if( !result.success )
        {
            return { scanEvents: [] };
        }

        if( ( result.data.scanEvents ?? [] ).length == 0 && ( userSettings.hasHealthMonitorWarning && isNotProduction() ) )
        {
            result.data.scanEvents = await useDemoScanHistory();
        }

        return result.data;
    }

    /**
     * Gets the error codes for the given scan event
     * @param scanEventId
     * @returns
     */
    async getErrorsForScanEvent( scanEventId: string ): Promise<{ errors: IErrorCodeDTO[]}>
    {
        const userSettings = useUserSettings();
        const result = await this.get( 'api/vehicle/scan-event/get-errors', { consumerVehicleTripId: scanEventId } );
        if( result.status == 404 && ( userSettings.hasHealthMonitorWarning && isNotProduction() ) )
        {
            result.data = {
                errors: await useDemoScanErrors()
            }
        }
        else if( result.success == false ) return { errors: [] }
        return result.data;
    }
}

export const useVehicleApi = () => new VehicleApi();
